/**
 * Terminology provider.
 *
 * Every visible string goes through `t("some.key")`. The bundle is fetched from
 * the API, which merges the shipped translations with this laboratory's
 * overrides - so when an administrator renames "Desk Head" to "Section Head",
 * the change appears everywhere without a deployment.
 *
 * The interface is English-only today, but the pipeline is per-locale: adding a
 * language later means shipping a new bundle, not reworking this provider.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { api } from "@/lib/api";
import { toTitleCase } from "@/lib/format";

import { DEFAULT_LOCALE, FALLBACK_LABELS, type LabelMap } from "./labels";

interface LabelBundle {
  locale: string;
  labels: LabelMap;
  version: string;
}

interface I18nValue {
  /** Translate a key. Falls back to the key's last segment, humanised. */
  t: (key: string, fallback?: string) => string;
  ready: boolean;
  version: string;
}

const I18nContext = createContext<I18nValue | null>(null);

/**
 * Namespaces whose entries are names rather than sentences: navigation, buttons,
 * entity names, field labels, statuses. They come back in title case whatever
 * case the bundle (or an administrator's override) wrote them in.
 */
const TITLE_NAMESPACES = [
  "nav.", "action.", "entity.", "field.", "status.", "evaluation.", "priority.", "source.", "role.",
];
const TITLE_KEYS = new Set(["workflow.timeline", "workflow.current_stage", "workflow.comment"]);

function isTitleKey(key: string): boolean {
  return TITLE_KEYS.has(key) || TITLE_NAMESPACES.some((prefix) => key.startsWith(prefix));
}

function humanise(key: string): string {
  const last = key.split(".").pop() ?? key;
  return last.replace(/_/g, " ").replace(/^./, (character) => character.toUpperCase());
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [labels, setLabels] = useState<LabelMap>(() => FALLBACK_LABELS[DEFAULT_LOCALE] ?? {});
  const [version, setVersion] = useState("fallback");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    api
      .get<LabelBundle>(`/v1/labels/${DEFAULT_LOCALE}`)
      .then((bundle) => {
        if (cancelled) return;
        setLabels(bundle.labels);
        setVersion(bundle.version);
        setReady(true);
      })
      .catch(() => {
        // Unauthenticated or offline: the fallback bundle is enough to render
        // the sign-in screen and an error state.
        if (!cancelled) setReady(true);
      });

    document.documentElement.lang = DEFAULT_LOCALE;
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<I18nValue>(
    () => ({
      t: (key, fallback) => {
        const label = labels[key] ?? fallback ?? humanise(key);
        return isTitleKey(key) ? toTitleCase(label) : label;
      },
      ready,
      version,
    }),
    [labels, ready, version],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used inside I18nProvider");
  }
  return context;
}

/** Shorthand for the common case of only needing the translator. */
export function useT(): I18nValue["t"] {
  return useI18n().t;
}
